import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Send, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { WhatsAppPreviewCard } from "@/components/app/WhatsAppPreviewCard";
import { WhatsAppLogsTable } from "@/components/app/WhatsAppLogsTable";

const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };

export default function WhatsAppPage() {
  const [date, setDate] = useState(tomorrow);
  const [preview, setPreview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const [p, l] = await Promise.all([
      http.get("/whatsapp/preview", { params: { date } }),
      http.get("/whatsapp/logs", { params: { limit: 100 } }),
    ]);
    setPreview(p.data); setLogs(l.data);
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const send = async (employeeId) => {
    const label = employeeId ? "Bu çalışana" : `${preview.items.length} çalışana`;
    if (!window.confirm(`${label} ${new Date(date).toLocaleDateString("tr-TR")} programı gönderilsin mi?`)) return;
    setBusy(employeeId || "all");
    try {
      const { data } = await http.post("/whatsapp/send", { date, employee_id: employeeId || null });
      const sent = data.results.filter((r) => r.status === "sent").length;
      const mock = data.results.filter((r) => r.status === "mock").length;
      const failed = data.results.filter((r) => r.status === "failed" || r.status === "skipped").length;
      if (mock) toast.warning(`${mock} mesaj hazırlandı (MOCK - API anahtarı tanımlı değil)`);
      if (sent) toast.success(`${sent} mesaj gönderildi`);
      if (failed) toast.error(`${failed} mesaj gönderilemedi`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-page">
      {preview && (preview.configured ? (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200" data-testid="whatsapp-config-ok">
          <CheckCircle2 className="h-4 w-4" /> WhatsApp Cloud API bağlı. Şablon: <code className="font-mono">{preview.template}</code>. Otomatik gönderim her akşam 20:00 (TR).
        </div>
      ) : (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm border border-amber-200" data-testid="whatsapp-config-warning">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <b>MOCK mod:</b> WhatsApp API anahtarları tanımlı değil. Mesajlar hazırlanıp kaydedilir ancak gerçekten gönderilmez.
            Backend <code className="font-mono">.env</code> dosyasına <code className="font-mono">WHATSAPP_ACCESS_TOKEN</code> ve <code className="font-mono">WHATSAPP_PHONE_NUMBER_ID</code> ekleyin; şablon <code className="font-mono">{preview.template}</code> (tr) Meta tarafından onaylı olmalıdır.
          </div>
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-3">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="whatsapp-date-input" />
        <span className="text-sm text-slate-500" data-testid="whatsapp-employee-count">{preview?.items.length || 0} çalışan · program önizlemesi</span>
        <Button className="ml-auto bg-emerald-600 hover:bg-emerald-700" disabled={!preview?.items.length || busy} onClick={() => send(null)} data-testid="whatsapp-send-all-button">
          <Send className="h-4 w-4 mr-2" /> Tümüne Şimdi Gönder
        </Button>
      </div>

      {preview?.items.length === 0 && (
        <Card className="p-12 text-center text-slate-400 border-slate-200" data-testid="whatsapp-empty">Bu tarihte çalışana atanmış iş bulunmuyor</Card>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {preview?.items.map((it) => (
          <WhatsAppPreviewCard key={it.employee_id} item={it} busy={busy === it.employee_id || busy === "all"} onSend={() => send(it.employee_id)} />
        ))}
      </div>

      <div>
        <h2 className="text-base md:text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: "Outfit" }}>Gönderim Geçmişi</h2>
        <WhatsAppLogsTable logs={logs} />
      </div>
    </div>
  );
}
